/**
 * Photo capture helpers. Compresses on capture (≤300kB target) so
 * IndexedDB doesn't blow up — typical phone photos are 3–5 MB raw.
 *
 * Photos are stored in photo_blobs keyed by [client_submission_id +
 * field_key], so the wizard can attach multiple photos under different
 * field labels (e.g. "well_cap", "equipment", "hazard"). The sync
 * engine's photo-upload hook (Phase 3.22b) reads from this table.
 */
import imageCompression from 'browser-image-compression';
import { getDb, type PhotoBlobRow } from '../db';

export const MAX_PHOTOS_PER_RECORD = 3;

const COMPRESSION_OPTS: Parameters<typeof imageCompression>[1] = {
  maxSizeMB: 0.3, // target 300 KB
  maxWidthOrHeight: 1600, // plenty for documentation; saves bytes
  useWebWorker: true,
  fileType: 'image/jpeg',
  initialQuality: 0.7,
};

export interface CapturedPhoto {
  key: string;
  blob: Blob;
  width_px: number | null;
  height_px: number | null;
  taken_at: number | null;
  size_bytes: number;
}

async function getImageDimensions(
  blob: Blob,
): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: null, height: null });
    };
    img.src = url;
  });
}

/**
 * Compress + persist a photo. `gpsHint` (lat/lon/accuracy) is captured
 * if the wizard has a current GPS fix, so the photo carries provenance
 * even if the user retries before sync.
 */
export async function capturePhoto(
  client_submission_id: string,
  field_key: string,
  file: File,
  gpsHint?: { lat: number; lon: number; accuracy_m: number } | null,
): Promise<CapturedPhoto> {
  if (!file.type.startsWith('image/')) {
    throw new Error(`Unsupported file type: ${file.type}`);
  }
  const compressed = await imageCompression(file, COMPRESSION_OPTS);
  const { width, height } = await getImageDimensions(compressed);
  const taken_at = file.lastModified || Date.now();

  const row: PhotoBlobRow = {
    key: `${client_submission_id}|${field_key}`,
    client_submission_id,
    field_key,
    blob: compressed,
    width_px: width,
    height_px: height,
    taken_at,
    gps_lat: gpsHint?.lat ?? null,
    gps_lon: gpsHint?.lon ?? null,
    gps_accuracy_m: gpsHint?.accuracy_m ?? null,
  };
  await getDb().photo_blobs.put(row);
  return {
    key: row.key,
    blob: compressed,
    width_px: width,
    height_px: height,
    taken_at,
    size_bytes: compressed.size,
  };
}

export async function listPhotos(
  client_submission_id: string,
): Promise<PhotoBlobRow[]> {
  return getDb()
    .photo_blobs.where('client_submission_id')
    .equals(client_submission_id)
    .toArray();
}

export async function removePhoto(key: string): Promise<void> {
  await getDb().photo_blobs.delete(key);
}

/** Move all photos from one client_submission_id to another. Used when
 *  the wizard reassigns a draft's UUID at submit time. */
export async function reassignPhotos(
  fromId: string,
  toId: string,
): Promise<void> {
  if (fromId === toId) return;
  const db = getDb();
  const rows = await db.photo_blobs
    .where('client_submission_id')
    .equals(fromId)
    .toArray();
  if (rows.length === 0) return;
  await db.transaction('rw', db.photo_blobs, async () => {
    for (const r of rows) {
      await db.photo_blobs.delete(r.key);
      const newKey = r.key.replace(fromId, toId);
      await db.photo_blobs.put({
        ...r,
        key: newKey,
        client_submission_id: toId,
      });
    }
  });
}
